import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store/store";
import { useGetUsersQuery } from "../../redux/api";
import { StatCard } from "../../components/StatCard";
import { IconUser, IconBuilding, IconBox } from "../../components/icons";

const initials = (name: string) =>
  (name ?? "")
    .split(" ")
    .map((p) => p.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

// Employee → Portal. The minimal workspace an organization employee sees.
const EmployeePortal = () => {
  const user = useSelector((s: RootState) => s.layout.user);
  const organization = useSelector((s: RootState) => s.layout.organization);

  const { data: users = [] } = useGetUsersQuery();

  const me = users.find(
    (u) => (u.email ?? "").toLowerCase() === (user?.email ?? "").toLowerCase(),
  );

  const displayName =
    (me ? `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim() : "") ||
    user?.name ||
    "Employee";

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="profile-header">
          <span className="profile-avatar">{initials(displayName)}</span>
          <div className="profile-id">
            <h2>{displayName}</h2>
            <span className="profile-email">{user?.email ?? "—"}</span>
            <div className="profile-badges">
              <span className="pill pill--neutral">{user?.role ?? "employee"}</span>
              {me?.status ? (
                <span className="badge badge--success">
                  <span className="badge__dot" />
                  {me.status}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <div className="stat-grid">
        <StatCard
          label="Organization"
          value={organization?.name ?? "—"}
          tone="primary"
          icon={<IconBuilding />}
        />
        <StatCard
          label="Role"
          value={user?.role ?? "employee"}
          tone="success"
          icon={<IconUser />}
        />
        <StatCard
          label="Portal"
          value="Employee"
          tone="warning"
          icon={<IconBox />}
        />
      </div>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Getting started</h2>
            <p className="panel__sub">Your day-to-day workspace</p>
          </div>
        </div>
        <div className="profile-empty">
          This is your employee portal. Tasks and assignments your organization
          shares with you will appear here.
        </div>
      </section>
    </div>
  );
};

export default EmployeePortal;
